import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Moon, Activity, Menu, X, Search, Settings, LogOut, ChevronDown, Shield, Stethoscope, Calendar, Heart, Info, Globe, Mail, Pill } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { t, lang, toggleLang, isRTL } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // Reconstruction du systeme labo dedie : `user.role` (issu de /api/auth/me)
  // ne distingue pas un membre d'hopital d'un membre de laboratoire pour le
  // role "establishment_member" (establishment_type vit sur db.clinics, pas
  // sur db.users). Meme sonde que Login.js::redirectForUser — /api/lab/me
  // renvoie un 403 propre (get_lab_establishment_context) si le compte n'est
  // pas rattache a un laboratoire, sans effet de bord.
  const [isLabMember, setIsLabMember] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (user?.role === 'establishment_member') {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lab/me`, { credentials: 'include' })
        .then((res) => { if (!cancelled) setIsLabMember(res.ok); })
        .catch(() => { if (!cancelled) setIsLabMember(false); });
    } else {
      setIsLabMember(false);
    }
    return () => { cancelled = true; };
  }, [user?.user_id, user?.role]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setProfileMenuOpen(false);
    setMobileMenuOpen(false);
  };

  const getDashboardLink = () => {
    if (!user) return '/';
    switch (user.role) {
      case 'admin': return '/admin';
      case 'doctor': return '/doctor/dashboard';
      case 'clinic_admin': return '/clinic/dashboard';
      case 'establishment_member': return isLabMember ? '/laboratoire' : '/hopital';
      default: return '/patient/dashboard';
    }
  };

  const getDashboardLabel = () => {
    if (!user) return t('nav_dashboard');
    switch (user.role) {
      case 'admin': return t('nav_admin');
      case 'doctor': return t('nav_doctor_cabinet');
      case 'clinic_admin': return 'Mon espace clinique';
      case 'establishment_member': return isLabMember ? 'Mon espace laboratoire' : 'Mon espace hopital';
      default: return t('nav_appointments');
    }
  };

  const getRoleLabel = () => {
    if (!user) return '';
    switch (user.role) {
      case 'admin': return t('role_admin');
      case 'doctor': return t('role_doctor');
      case 'clinic_admin': return 'Clinique';
      case 'establishment_member': return isLabMember ? 'Laboratoire' : 'Hopital';
      default: return t('role_patient');
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="bg-white border-b border-[#E2E8F0] sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 flex-shrink-0" data-testid="navbar-logo">
            <div className="flex items-center text-[#0596DE]">
              <Moon className="w-7 h-7" />
              <Activity className={`w-7 h-7 ${isRTL ? '-mr-2' : '-ml-2'}`} />
            </div>
            <span className="font-bold text-xl text-[#0596DE] font-['Outfit']">Tabbibak.com</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/search"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/search') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
              data-testid="nav-search"
            >
              <Search className="w-4 h-4" />
              {t('nav_search')}
            </Link>

            {/* Annuaire des pharmacies (patients) — sans ce lien, aucun moyen
                de decouvrir /pharmacies depuis le site (constat Nadjib
                2026-09-05) : accessible uniquement en tapant l'URL a la main. */}
            <Link
              to="/pharmacies"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/pharmacies') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
              data-testid="nav-pharmacies"
            >
              <Pill className="w-4 h-4" />
              Pharmacies
            </Link>

            <Link
              to="/about"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/about') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
              data-testid="nav-about"
            >
              <Info className="w-4 h-4" />
              {t('nav_about')}
            </Link>

            <Link
              to="/contact"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/contact') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
              data-testid="nav-contact"
            >
              <Mail className="w-4 h-4" />
              Contact
            </Link>
            <Link
              to="/blog"
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                isActive('/blog') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
              }`}
            >
              Blog Santé
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to={getDashboardLink()}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    isActive(getDashboardLink()) ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                  }`}
                  data-testid="nav-dashboard"
                >
                  {user?.role === 'admin' ? <Shield className="w-4 h-4" /> :
                   user?.role === 'doctor' ? <Stethoscope className="w-4 h-4" /> :
                   <Calendar className="w-4 h-4" />}
                  {getDashboardLabel()}
                </Link>

                {user?.role === 'patient' && (
                  <Link
                    to="/favorites"
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      isActive('/favorites') ? 'bg-[#E6F4FB] text-[#0596DE]' : 'text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A]'
                    }`}
                    data-testid="nav-favorites"
                  >
                    <Heart className="w-4 h-4" />
                    {t('nav_favorites')}
                  </Link>
                )}
              </>
            )}
          </div>

          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={toggleLang}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors border border-[#E2E8F0]"
              data-testid="lang-switcher"
              title={lang === 'fr' ? 'العربية' : 'Français'}
            >
              <Globe className="w-4 h-4" />
              <span>{lang === 'fr' ? 'AR' : 'FR'}</span>
            </button>

            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-[#F8FAFC] transition-colors"
                  data-testid="profile-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-[#E6F4FB] flex items-center justify-center">
                    <span className="text-sm font-bold text-[#0596DE]">
                      {user?.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-[#0F172A] max-w-[120px] truncate">{user?.name}</span>
                  <ChevronDown className={`w-4 h-4 text-[#64748B] transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`} />
                </button>

                {profileMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setProfileMenuOpen(false)} />
                    <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-64 bg-white rounded-xl border border-[#E2E8F0] shadow-lg z-50 overflow-hidden`} data-testid="profile-dropdown">
                      <div className="px-4 py-3 border-b border-[#E2E8F0] bg-[#F8FAFC]">
                        <p className="text-sm font-medium text-[#0F172A]">{user?.name}</p>
                        <p className="text-xs text-[#64748B]">{user?.email}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user?.role === 'admin' ? 'bg-purple-100 text-purple-600' :
                          user?.role === 'doctor' ? 'bg-[#E6F4FB] text-[#0596DE]' :
                          'bg-[#F1F5F9] text-[#475569]'
                        }`}>
                          {getRoleLabel()}
                        </span>
                      </div>

                      <div className="py-1">
                        <Link
                          to={getDashboardLink()}
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                          data-testid="dropdown-dashboard"
                        >
                          <Calendar className="w-4 h-4" />
                          {getDashboardLabel()}
                        </Link>

                        <Link
                          to="/settings"
                          onClick={() => setProfileMenuOpen(false)}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                          data-testid="dropdown-settings"
                        >
                          <Settings className="w-4 h-4" />
                          {t('nav_settings')}
                        </Link>

                        {user?.role === 'patient' && (
                          <Link
                            to="/favorites"
                            onClick={() => setProfileMenuOpen(false)}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                            data-testid="dropdown-favorites"
                          >
                            <Heart className="w-4 h-4" />
                            {t('nav_my_favorites')}
                          </Link>
                        )}

                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 w-full transition-colors"
                          data-testid="dropdown-logout"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('nav_logout')}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login">
                  <button className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors">
                    {t('nav_login')}
                  </button>
                </Link>
                <Link to="/register" data-testid="nav-register">
                  <button className="px-5 py-2 text-sm font-medium text-white bg-[#0596DE] hover:bg-[#047CBA] rounded-full transition-colors">
                    {t('nav_register')}
                  </button>
                </Link>
              </>
            )}
          </div>

          <div className="flex items-center gap-2 md:hidden">
            <button
              onClick={toggleLang}
              className="p-2 rounded-lg hover:bg-[#F8FAFC] text-sm font-medium text-[#475569]"
              data-testid="mobile-lang-switcher"
            >
              <Globe className="w-5 h-5" />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-[#F8FAFC]"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#E2E8F0] bg-white" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <Link
              to="/search"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
            >
              <Search className="w-5 h-5" />
              {t('nav_search')}
            </Link>

            <Link
              to="/pharmacies"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
            >
              <Pill className="w-5 h-5" />
              Pharmacies
            </Link>

            <Link
              to="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
            >
              <Info className="w-5 h-5" />
              {t('nav_about')}
            </Link>

            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
            >
              <Mail className="w-5 h-5" />
              Contact
            </Link>

            {isAuthenticated && (
              <>
                <Link
                  to={getDashboardLink()}
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                >
                  <Calendar className="w-5 h-5" />
                  {getDashboardLabel()}
                </Link>

                {user?.role === 'patient' && (
                  <Link
                    to="/favorites"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                  >
                    <Heart className="w-5 h-5" />
                    {t('nav_my_favorites')}
                  </Link>
                )}

                <Link
                  to="/settings"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors"
                >
                  <Settings className="w-5 h-5" />
                  {t('nav_settings')}
                </Link>

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 hover:bg-red-50 w-full transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  {t('nav_logout')}
                </button>
              </>
            )}

            {!isAuthenticated && (
              <div className="pt-2 border-t border-[#E2E8F0] space-y-2">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full px-4 py-3 text-sm font-medium text-[#475569] hover:bg-[#F8FAFC] rounded-xl transition-colors">
                    {t('nav_login')}
                  </button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <button className="w-full px-4 py-3 text-sm font-medium text-white bg-[#0596DE] hover:bg-[#047CBA] rounded-full transition-colors">
                    {t('nav_register')}
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;